import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatApiErrorDetail } from "@/lib/api";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "consumer" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const u = await register(form);
      if (u.role === "business_owner") navigate("/dashboard");
      else navigate("/directory");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="register-page">
      <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Create your account</h1>
      <p className="text-slate-500 mt-2 mb-8">Join Domain Host Directory to review, claim, or list a business.</p>
      <form onSubmit={onSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-2">Full name</label>
          <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="register-name" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-2">Email</label>
          <Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="register-email" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-2">Password (min 8 chars)</label>
          <Input type="password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="register-password" />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block mb-3">I am joining as</label>
          <RadioGroup value={form.role} onValueChange={(v) => set("role", v)} className="grid grid-cols-2 gap-3">
            <label className={`cursor-pointer border rounded-lg p-4 ${form.role === "consumer" ? "border-blue-700 bg-blue-50" : "border-slate-200"}`}>
              <RadioGroupItem value="consumer" className="sr-only" data-testid="register-role-consumer" />
              <div className="text-sm font-semibold text-slate-900">A consumer</div>
              <div className="text-xs text-slate-500 mt-1">Read & write reviews</div>
            </label>
            <label className={`cursor-pointer border rounded-lg p-4 ${form.role === "business_owner" ? "border-blue-700 bg-blue-50" : "border-slate-200"}`}>
              <RadioGroupItem value="business_owner" className="sr-only" data-testid="register-role-owner" />
              <div className="text-sm font-semibold text-slate-900">A business owner</div>
              <div className="text-xs text-slate-500 mt-1">Claim & manage listings</div>
            </label>
          </RadioGroup>
        </div>
        {error && <div className="text-sm text-red-600" data-testid="register-error">{error}</div>}
        <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="register-submit">
          {loading ? "Creating…" : "Create account"}
        </Button>
        <div className="text-sm text-slate-500 text-center">
          Already have an account? <Link to="/login" className="text-blue-700 hover:underline">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
